import React, { ReactElement, ReactNode, useContext } from 'react'
import { StatusMessage } from 'is24-corecss'
import { createContext, useState, useCallback } from 'react'
import styles from './ToastContext.module.css'
export const ToastContext = createContext<ToastContextProps>(null!)

export type Toast = {
  headline: string
  message: string | ReactElement
  type: 'info' | 'confirm' | 'error'
  timeout?: number
}

type ToastContextProps = { addToast: (toast: Toast) => void; closeToast: (toast: Toast) => void }

type Props = {
  children?: ReactNode
}

const DEFAULT_TIME_OUT = 4000

export const ToastContextProvider = ({ children }: Props) => {
  const [toasts, setToasts] = useState<Toast[]>([])

  const closeToast = useCallback(
    function (toast: Toast) {
      setToasts((toasts: Toast[]) => toasts.filter((t) => t !== toast))
    },
    [setToasts]
  )

  const addToast = useCallback(
    function (toast: Toast) {
      if (!toasts.map((t) => t.headline).includes(toast.headline)) {
        setToasts((toasts: Toast[]) => [...toasts, toast])
        setTimeout(() => closeToast(toast), toast.timeout || DEFAULT_TIME_OUT)
      }
    },
    [toasts, setToasts, closeToast]
  )

  return (
    <ToastContext.Provider value={{ addToast, closeToast }}>
      {toasts.length > 0 && (
        <div className={`${styles.toastsWrapper} margin-bottom-m`}>
          {toasts.map((toast) => (
            <StatusMessage
              key={toast.headline}
              headline={toast.headline}
              message={toast.message}
              statusType={toast.type}
              statusStyle='toast'
              onClose={() => closeToast(toast)}
            />
          ))}
        </div>
      )}
      {children}
    </ToastContext.Provider>
  )
}

export function useToastContext() {
  return useContext(ToastContext)
}
